import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import DropVoteProgressing from "@/components/drops/view/utils/DropVoteProgressing";
import ParticipationDropVoteDetailsTrigger from "@/components/waves/drops/participation/ratings/ParticipationDropVoteDetailsTrigger";
import { WAVE_VOTING_LABELS, WAVE_VOTE_STATS_LABELS } from "@/helpers/waves/waves.constants";
import { formatApprovalCountdownTime } from "@/helpers/waves/approve-wave.helpers";
import { useApprovalDropStatus } from "@/hooks/waves/useApprovalDropStatus";
interface WaveLeaderboardDropRatersProps {
  readonly drop: ExtendedDrop;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
}
export const WaveLeaderboardDropRaters: React.FC<WaveLeaderboardDropRatersProps> = t0 => {
  const $ = _c(43);
  const {
    drop,
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed: t1
  } = t0;
  const isVotingClosed = t1 === undefined ? false : t1;
  const totalVote = drop.rating;
  const userVote = drop.context_profile_context?.rating ?? 0;
  const votingLabel = WAVE_VOTING_LABELS[drop.wave.voting_credit_type];
  const totalVoteClass = totalVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const userVoteClass = userVote < 0 ? "tw-text-rose-400" : "tw-text-iron-50";
  const displayWinningThreshold = typeof winningThreshold === "number" && winningThreshold > 0 ? winningThreshold : null;
  const hasWinningThreshold = displayWinningThreshold !== null;
  let t2;
  if ($[0] !== displayWinningThreshold || $[1] !== drop || $[2] !== isVotingClosed || $[3] !== winningThresholdMinDurationMs) {
    t2 = {
      drop,
      isClosed: isVotingClosed,
      winningThreshold: displayWinningThreshold,
      winningThresholdMinDurationMs
    };
    $[0] = displayWinningThreshold;
    $[1] = drop;
    $[2] = isVotingClosed;
    $[3] = winningThresholdMinDurationMs;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const approvalStatus = useApprovalDropStatus(t2);
  let approvalStatusClass;
  if (approvalStatus.kind === "approved" || approvalStatus.kind === "approving" || approvalStatus.kind === "reached_threshold") {
    approvalStatusClass = "tw-text-emerald-400";
  } else {
    if (approvalStatus.kind === "closed") {
      approvalStatusClass = "tw-text-amber-300";
    }
  }
  let t3;
  bb0: {
    if (!hasWinningThreshold) {
      t3 = null;
      break bb0;
    }
    if (approvalStatus.kind === "approved") {
      t3 = "Approved";
      break bb0;
    }
    if (approvalStatus.kind === "approving") {
      const t4 = approvalStatus.countdownMs ?? 0;
      let t5;
      if ($[5] !== t4) {
        t5 = formatApprovalCountdownTime(t4);
        $[5] = t4;
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      t3 = `Approving in ${t5}`;
      break bb0;
    }
    if (approvalStatus.kind === "reached_threshold") {
      t3 = "Reached threshold";
      break bb0;
    }
    if (approvalStatus.kind === "closed") {
      t3 = "Closed";
      break bb0;
    }
    const t4 = approvalStatus.remaining ?? 0;
    let t5;
    if ($[7] !== t4) {
      t5 = formatNumberWithCommas(t4);
      $[7] = t4;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t3 = `Needs ${t5}`;
  }
  const approvalStatusLabel = t3;
  const hasUserVoted = userVote !== 0;
  const t4 = `tw-font-medium ${totalVoteClass}`;
  let t5;
  if ($[9] !== totalVote) {
    t5 = formatNumberWithCommas(totalVote);
    $[9] = totalVote;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== displayWinningThreshold) {
    t7 = displayWinningThreshold !== null && <><span className="tw-font-medium tw-text-iron-500">/</span><span className="tw-font-medium tw-text-iron-50">{formatNumberWithCommas(displayWinningThreshold)}</span></>;
    $[14] = displayWinningThreshold;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = hasWinningThreshold ? drop.realtime_rating : drop.rating_prediction;
  const t9 = hasWinningThreshold ? "Votes given now" : undefined;
  let t10;
  if ($[16] !== drop.rating || $[17] !== t8 || $[18] !== t9) {
    t10 = <DropVoteProgressing current={drop.rating} projected={t8} tooltipLabel={t9} compact={true} />;
    $[16] = drop.rating;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t6 || $[22] !== t7) {
    t11 = <div className="tw-relative tw-inline-flex tw-items-center tw-gap-x-1.5">{t6}{t7}{t10}</div>;
    $[20] = t10;
    $[21] = t6;
    $[22] = t7;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== approvalStatusClass || $[25] !== approvalStatusLabel || $[26] !== hasWinningThreshold || $[27] !== votingLabel) {
    t12 = <span className="tw-whitespace-nowrap tw-font-normal tw-text-iron-400">{hasWinningThreshold && approvalStatusLabel !== null ? <span className={approvalStatusClass}>{approvalStatusLabel}</span> : <>{votingLabel} {WAVE_VOTE_STATS_LABELS.TOTAL}</>}</span>;
    $[24] = approvalStatusClass;
    $[25] = approvalStatusLabel;
    $[26] = hasWinningThreshold;
    $[27] = votingLabel;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <div className="tw-flex tw-items-center tw-gap-x-2 tw-text-sm tw-leading-5">{t11}{t12}</div>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== drop) {
    t14 = <div className="tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap tw-text-sm tw-leading-5"><ParticipationDropVoteDetailsTrigger drop={drop} /></div>;
    $[32] = drop;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== hasUserVoted || $[35] !== userVote || $[36] !== userVoteClass || $[37] !== votingLabel) {
    t15 = hasUserVoted && <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-leading-5"><span className="tw-whitespace-nowrap"><span className="tw-font-normal tw-text-iron-400">{WAVE_VOTE_STATS_LABELS.YOUR_VOTES}:{" "}</span><span className={`tw-font-medium ${userVoteClass}`}>{formatNumberWithCommas(userVote)} {votingLabel}</span></span></div>;
    $[34] = hasUserVoted;
    $[35] = userVote;
    $[36] = userVoteClass;
    $[37] = votingLabel;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t13 || $[40] !== t14 || $[41] !== t15) {
    t16 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 sm:tw-justify-end">{t13}{t14}{t15}</div>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
};